import toast from 'react-hot-toast'
import { DeliveryRunDetail, Order } from '../types/order'
import { formatTime, parseApiDate } from './format'
import { RESTAURANT_ADDRESS } from './geocode'

// First-party delivery domain helpers, shared by DeliveryBoardPage and DriverPage.
// Extracted when the driver screen was added: the address-join rule below encodes a
// live data quirk, and two silently-divergeable copies of it is how the same order
// ends up rendering one address on the FOH board and another on the phone the
// driver is actually navigating with.

export interface DeliveryAddress {
  line1: string
  line2: string
}

// Deliberately tolerant of a NULL house number: Thuisbezorgd self-delivery orders
// carry the number INSIDE delivery_street (splitting Dutch addresses by regex is
// guesswork server-side), so both parts are joined and empties dropped rather than
// formatted with a separator.
export const addressOf = (order: Order): DeliveryAddress => ({
  line1: [order.delivery_street, order.delivery_house_number].filter(Boolean).join(' '),
  line2: [order.delivery_postcode, order.delivery_city].filter(Boolean).join(' '),
})

export const hasAddress = (order: Order): boolean => {
  const { line1, line2 } = addressOf(order)
  return Boolean(line1 || line2)
}

// "Ours to drive" — the single predicate behind every own-delivery rule in the POS:
// the kitchen may not complete these (the driver's claim is the handover), the delivery
// board prints them a bezorgbon, and only they get the IN_DELIVERY completion fallback.
// Mirrors the API's guard (OrderService: order_type 'delivery' && delivered_by 'own').
// delivered_by is NULL on non-delivery orders and fails safe to 'platform' for an
// unrecognised HubRise channel, so an unknown channel is never treated as ours.
export const isOwnDelivery = (order: Pick<Order, 'order_type' | 'delivered_by'>): boolean =>
  order.order_type === 'DELIVERY' && order.delivered_by === 'own'

// A HubRise self-delivery order can be 'own' with a null address, and the API 400s the
// slip for those. Mirrors the route's own condition EXACTLY (routes/receipt.ts:117:
// `order_type !== 'delivery' || !order.delivery_street`) — deliberately not the looser
// hasAddress(), which passes on a postcode alone and would still open a tab of raw JSON.
export const canPrintDeliverySlip = (order: Order): boolean =>
  isOwnDelivery(order) && Boolean(order.delivery_street)

// We drive it, but it was placed via a platform (Thuisbezorgd / Uber Eats): the customer
// follows the order in the platform app and never receives our tracking link. These are
// the orders the board offers a copyable WhatsApp message for (owner decision 2026-09-10:
// the operator pastes it and types the number by hand — no messaging integration).
export const isOwnPlatformDelivery = (
  order: Pick<Order, 'order_type' | 'delivered_by' | 'order_source'>
): boolean => isOwnDelivery(order) && order.order_source === 'HUBRISE'

// The copyable WhatsApp text — change the wording here only (owner's English draft,
// 2026-09-10). The link sentence appears only when the order carries a `tracking_url`: the
// API issues a token at HubRise ingest for own-delivery orders, so orders ingested before
// that shipped have none (no backfill) and copy the message without a link. Our internal
// order number is left out on purpose: a Thuisbezorgd customer only knows the platform's
// number. Contract: ../../API/docs/frontend/pos-platform-tracking-link.md.
export const whatsAppMessage = (
  order: Pick<Order, 'customer_name'>,
  trackingUrl?: string | null
): string => {
  const firstName = order.customer_name?.trim().split(/\s+/)[0]
  const greeting = firstName ? `Hi ${firstName},` : 'Hi,'
  const tracking = trackingUrl ? ` You can track it here: ${trackingUrl}` : ''
  return `${greeting} we are working on your Curry Me order.${tracking}`
}

// Built client-side — no geocoding call, no API key. Google resolves the free-text
// destination the same way typing it into Maps would.
const destinationOf = (order: Order): string => {
  const { line1, line2 } = addressOf(order)
  return [line1, line2].filter(Boolean).join(', ')
}

export const mapsUrl = (order: Order): string =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destinationOf(order))}`

// A delivery route holds at most 6 stops (API: `Delivery run can hold at most `), and one
// route is one Google Maps trip, so the multi-stop links share that cap. A PC4 bundle
// suggestion larger than a route could never be driven as one run anyway. Google takes at
// most 9 waypoints in a desktop browser (3 in a mobile browser; the Maps app takes more), and
// a round trip spends every stop on a waypoint — 6 still fits. The button label must say how
// many stops the link really carries (runStopCount), so it never disagrees with the
// "Print N" beside it.
export const MAX_RUN_STOPS = 6

export const runStopCount = (orders: Order[]): number => Math.min(orders.length, MAX_RUN_STOPS)

// The restaurant door as Google should read it — the door the API routes every run from.
// Text, not coordinates, so the trip shows a readable start and end instead of a dropped pin.
const RESTAURANT_MAPS_ADDRESS = [
  `${RESTAURANT_ADDRESS.street} ${RESTAURANT_ADDRESS.houseNumber}`,
  `${RESTAURANT_ADDRESS.postcode} ${RESTAURANT_ADDRESS.city}`,
].join(', ')

// A stop as one segment of a /maps/dir/ path: percent-encoded (a "/" inside an address
// would otherwise read as a stop boundary), spaces as "+", the form Google's own share links
// use.
const mapsPathSegment = (destination: string): string =>
  encodeURIComponent(destination).replace(/%20/g, '+')

// One directions link for the stops a DRIVER still has to ride, in the given order — the
// PATH form `/maps/dir//stop1/stop2/stop3`, NOT the `api=1&waypoints=` query form the board
// uses below. Google's URL docs: waypoints "are not supported on all Google Maps products; in
// those cases this parameter will be ignored" — the Maps app a phone hands this link to is
// such a product, and it opened a two-stop route as directions to the LAST stop only (driver
// report, 2026-10-02). The path form is what the Maps app itself produces when a multi-stop
// trip is shared, so every stop survives there and in a mobile browser alike. The empty first
// segment is the origin, left to the device: Google starts at the phone's own location, which
// is right for a driver already out on the road. Still keyless and free — the driver's app does
// the navigating and can reorder the stops itself. This is the deliberate alternative to an
// on-map routing service (tasks/todo.md §0a).
export const runMapsUrl = (orders: Order[]): string => {
  const stops = orders.map(destinationOf).filter(Boolean).slice(0, MAX_RUN_STOPS)
  return `https://www.google.com/maps/dir//${stops.map(mapsPathSegment).join('/')}`
}

// The same run as the OPERATOR plans it (owner ask 2026-09-11): out from the restaurant,
// every stop in order, and back to the restaurant — so Google's total is the time the driver
// is away, not only the ride to the last door. Bicycling, because the drivers ride e-bikes
// (the API computes its own arrival estimates with a cycling-electric profile for the same
// reason); a car route skips the cycle paths and one-way cut-throughs and misstates the
// duration. Google's bike times assume a regular bike, so they read on the long side.
// Stays on the `api=1` query form, unlike runMapsUrl above: the board runs in a terminal's
// browser, where waypoints work (nine on a desktop), and only this form has a documented
// travelmode. Opened on a phone's Maps app it would show the restaurant-to-restaurant trip
// without the stops — the board is not a phone screen.
export const roundTripMapsUrl = (orders: Order[]): string => {
  const stops = orders.map(destinationOf).filter(Boolean).slice(0, MAX_RUN_STOPS)
  const params = new URLSearchParams({
    api: '1',
    origin: RESTAURANT_MAPS_ADDRESS,
    destination: RESTAURANT_MAPS_ADDRESS,
    travelmode: 'bicycling',
  })
  if (stops.length > 0) params.set('waypoints', stops.join('|'))
  return `https://www.google.com/maps/dir/?${params.toString()}`
}

// --- Delivery routes (pos-delivery-run-contract.md) --------------------------------------

/** A route's stops in DELIVERY order. The API already returns them so; sorting on
 *  delivery_run_position keeps it true for any list assembled or edited client-side. A stop
 *  cancelled or refunded on the road has `delivery_run_position: null` (dense numbering, API
 *  2026-10-08) and stays on the route for the record — it sorts LAST, never to the top. */
export const stopsInOrder = (stops: Order[]): Order[] =>
  [...stops].sort(
    (a, b) => (a.delivery_run_position ?? Infinity) - (b.delivery_run_position ?? Infinity)
  )

/** The stops a driver actually rides to, in delivery order: cancelled and refunded stops
 *  drop out, delivered ones stay (they were ridden). The same set as the API's
 *  `stops_total`. */
export const countedStops = (stops: Order[]): Order[] =>
  stopsInOrder(stops).filter(stop => stop.status !== 'CANCELLED' && stop.status !== 'REFUNDED')

/** Stop numbers as a person counts them: 1..N over countedStops. Since 2026-10-08 the API
 *  numbers the same way (`delivery_run_position` is dense over the counted stops, null on a
 *  cancelled or refunded one), so this equals the raw field; it stays so every screen keeps
 *  one source and a route edited client-side before the next poll still counts right.
 *  Cancelled and refunded stops get no number. */
export const stopNumbers = (stops: Order[]): Map<number, number> =>
  new Map(countedStops(stops).map((stop, index): [number, number] => [stop.id, index + 1]))

/** Stops still on the road (IN_DELIVERY), in order. Delivered and cancelled stops keep
 *  their positions but are done — the driver's Maps link and "Change order" concern only
 *  these, and reorderDeliveryRun must receive exactly this set. */
export const openStops = (stops: Order[]): Order[] =>
  stopsInOrder(stops).filter(o => o.status === 'IN_DELIVERY')

/** Stops that would block Start: anything not READY. The API's own condition
 *  (`Delivery run has orders that are not ready`), shown BEFORE the tap. */
export const stopsNotReady = (run: Pick<DeliveryRunDetail, 'stops'>): Order[] =>
  run.stops.filter(o => o.status !== 'READY')

/** A PLANNED route the assigned driver can start right now. */
export const canStartRun = (run: Pick<DeliveryRunDetail, 'status' | 'stops' | 'driver'>): boolean =>
  run.status === 'PLANNED' && Boolean(run.driver) && run.stops.length > 0 && stopsNotReady(run).length === 0

/** "≈ 18:55" — an estimate, never the promise (scheduled_for is untouched). null means no
 *  estimate: routing not configured, the call failed, or a stop lacks coordinates. Render
 *  null as nothing, never as "late". */
export const formatEta = (iso?: string | null): string | null =>
  iso && parseApiDate(iso) ? `≈ ${formatTime(iso)}` : null

/** An estimate this far in the past is OVERDUE on the board (owner-approved, 2026-09-30). */
export const OVERDUE_AFTER_MS = 5 * 60_000

export interface EtaLabel {
  /** "≈ 18:56", or "≈ 18:56 · 9 min over" once overdue. */
  text: string
  overdue: boolean
}

/** The board's reading of an estimate: "≈ 18:56", turning amber with "· n min over" once it is
 *  more than 5 minutes past on the terminal's clock (`now`) — instead of disappearing. An
 *  operator reads a passed estimate as the cue to call; the webshop keeps hiding it, because a
 *  customer reads it as a broken promise. null = no estimate, never "late". */
export const etaLabel = (iso: string | null | undefined, now: number): EtaLabel | null => {
  const date = parseApiDate(iso)
  if (!date) return null
  const over = now - date.getTime()
  if (over <= OVERDUE_AFTER_MS) return { text: `≈ ${formatTime(iso as string)}`, overdue: false }
  return { text: `≈ ${formatTime(iso as string)} · ${Math.floor(over / 60_000)} min over`, overdue: true }
}

/** The real moment a stop was delivered — the status change itself — shown where a stop still
 *  out shows its estimate. `current_status_since` has an explicit ISO resolver on the API. */
export const deliveredAt = (stop: Pick<Order, 'status' | 'current_status_since'>): string | null =>
  stop.status === 'COMPLETED' && stop.current_status_since ? formatTime(stop.current_status_since) : null

/** The store as the closing row of a route: "Rademarkt 10" — the door every route returns to. */
export const STORE_DOOR = `${RESTAURANT_ADDRESS.street} ${RESTAURANT_ADDRESS.houseNumber}`

// Delivery slip ("bezorgbon") — the API's third receipt type: an operational,
// deliberately money-free driver print (no amounts, no VAT, no company header), so it
// can never be mistaken for the fiscal receipt. Same open-in-new-tab + print=1 pattern
// as ReceiptUtils, but keyed on order_number: the trailing "-0" is REQUIRED — the
// receipt route parses the order number by dropping the last dash-segment. Public
// within 10 days of the order, so no auth token in the URL for the same-day flow.
// Contract: API repo docs/frontend/pos-delivery-driver-contract.md, "Delivery slip".
const API_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:4000/graphql')
  .replace(/\/graphql\/?$/, '')

export const deliverySlipUrl = (orderNumber: string): string =>
  `${API_ORIGIN}/receipt/RCP-${orderNumber}-0?type=DELIVERY&print=1`

// Open one slip per order, each in its own tab with print=1 (bundle-print v1 per the API
// contract). Shared by the delivery board (per card + per PC4 bundle) and the order detail
// page's Receipts dropdown, so both entry points behave identically. Browsers typically
// allow only the first popup of a gesture until the user permits popups for this origin —
// a one-time browser setting per POS terminal — so any blocked tab gets a toast pointing
// there instead of failing silently. A single batch print is the API's planned v2,
// deferred until sequential printing proves annoying in practice.
export const openDeliverySlips = (orders: Pick<Order, 'order_number'>[]) => {
  const blocked = orders.filter(o => !window.open(deliverySlipUrl(o.order_number), '_blank'))
  if (blocked.length > 0) {
    toast.error(
      `${blocked.length} of ${orders.length} slips were blocked — allow popups for this site to print bundles.`
    )
  }
}

// Stable wire-contract prefix for a lost claim race (suffix = the winning driver's
// name). Part of the API contract (tasks/delivery-driver-api-reply.md): the API's
// extensions.code is still INTERNAL_SERVER_ERROR across the board, so this prefix —
// never the full string — is the ONLY reliable discriminator. DriverPage pins the
// message inline on the card it belongs to; the global errorLink skips toasting it
// for the same reason (a toast would double-surface and outlive the card).
export const CLAIM_CONFLICT_PREFIX = 'Delivery already claimed by '
